// FEMO — Formulario de Evaluación Médica Ocupacional (Módulo H, Grupo 5).
// Igual que 024 (MotorFormularioConsentimiento.tsx): wizard con un paso de
// firma manuscrita entre medio, así que no pasa por MotorFormulario.tsx
// (Grupo 1) directamente. Diferencia real con 024: acá la firma manuscrita
// (bloque P, "Firma o huella del trabajador") es del TRABAJADOR, no de un
// paciente en sentido clínico — mismo Patient/`subject` de la ficha, mismo
// componente `CapturaFirmaPaciente`, aplicado a este contexto. El bloque O
// ("Datos del profesional... firma y sello") es exactamente el punto de
// enganche con `PanelFirmaImpresion` — no hace falta un paso propio para
// ese bloque, es la firma profesional de siempre. A diferencia de 024, FEMO
// no arma un recurso FHIR adicional (Consent) al firmar — el
// QuestionnaireResponse + las dos firmas (trabajador + profesional) ya son
// el documento completo.
import type { DocumentReference, Encounter, Patient, Questionnaire, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { InsigniaFormularioPropio } from '@/components/InsigniaFormularioPropio';
import { codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import type { PatientChartOutletContext } from '@/paciente/PatientChartPage';
import { CapturaFirmaPaciente, type FirmaPacienteCapturada } from './CapturaFirmaPaciente';
import { FormularioQuestionnaire } from './FormularioQuestionnaire';
import { PanelFirmaImpresion } from './PanelFirmaImpresion';
import { edadMsp, formatoEdadMsp, type DatosPacienteFemo } from '@logiacare/hce-pdf';

const CODIGO = 'FEMO';

type Estado =
  | { fase: 'cargando' }
  | { fase: 'no-encontrado' }
  | { fase: 'error'; mensaje: string }
  | { fase: 'formulario'; questionnaire: Questionnaire }
  | { fase: 'firma-trabajador'; questionnaire: Questionnaire; response: QuestionnaireResponse }
  | {
      fase: 'firma-profesional';
      questionnaire: Questionnaire;
      response: QuestionnaireResponse;
      firmaTrabajadorDocRefId: string;
      expedienteFirmado: boolean;
      certificadoFirmado: boolean;
    }
  | { fase: 'procesando'; mensaje: string }
  | { fase: 'listo' };

const PASOS = ['pasoFormulario', 'pasoFirmaTrabajador', 'pasoFirmaProfesional', 'pasoListo'] as const;

function pasoActivo(fase: Estado['fase']): number {
  if (fase === 'formulario') return 0;
  if (fase === 'firma-trabajador') return 1;
  if (fase === 'firma-profesional' || fase === 'procesando') return 2;
  if (fase === 'listo') return 3;
  return 0;
}

/** `TFunction` de react-i18next es un tipo genérico incómodo de anotar acá — alcanza con `(clave: string) => string`, el único uso real. */
function datosPacienteFemo(paciente: Patient, t: (clave: string) => string): DatosPacienteFemo {
  const edad = edadMsp(paciente.birthDate);
  return {
    nombre: paciente.name?.[0] ? `${paciente.name[0].given?.join(' ') ?? ''} ${paciente.name[0].family ?? ''}`.trim() : '',
    identificador: paciente.identifier?.[0]?.value,
    sexo: paciente.gender ? t(`fichaPaciente.genero.${paciente.gender}`) : undefined,
    fechaNacimiento: paciente.birthDate,
    edad: edad ? formatoEdadMsp(edad) : undefined,
  };
}

function dataUrlABytes(dataUrl: string, contentTypeFallback: string): { bytes: Uint8Array; contentType: string } {
  const match = /^data:([^;]*);base64,(.*)$/.exec(dataUrl);
  const contentType = match?.[1] || contentTypeFallback;
  const b64 = match ? match[2] : dataUrl;
  const binario = atob(b64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return { bytes, contentType };
}

function Indicador({ fase }: { fase: Estado['fase'] }): JSX.Element {
  const { t } = useTranslation();
  const activo = pasoActivo(fase);
  return (
    <ol className="flex flex-wrap gap-4 text-xs font-mono uppercase tracking-wide">
      {PASOS.map((paso, i) => (
        <li key={paso} className={i === activo ? 'font-semibold text-hce-pine' : i < activo ? 'text-hce-ink' : 'text-hce-muted'}>
          {i + 1}. {t(`femo.${paso}`)}
        </li>
      ))}
    </ol>
  );
}

export function FormularioFemoPage(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const encounterParam = searchParams.get('encounter');
  const especialidad = useEspecialidadDelPracticante();

  const subject = useMemo<Reference<Patient>>(() => ({ reference: `Patient/${id}` }), [id]);
  const encounter = useMemo<Reference<Encounter> | undefined>(() => (encounterParam ? { reference: encounterParam } : undefined), [encounterParam]);

  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });
  // Descartar (pedido explícito de Sue 2026-09-27): SOLO existe en el paso
  // 'formulario', antes de guardar — ver `handleDescartar` más abajo.
  const [descartado, setDescartado] = useState(false);
  // Bloqueo de salida sin firmar/descartar (pedido explícito de Sue,
  // 2026-09-27) — mismo criterio que MotorFormularioConsentimiento.tsx.
  const reportarPendienteFirma = useOutletContext<PatientChartOutletContext | undefined>()?.reportarPendienteFirma;
  const pendienteFirma = estado.fase !== 'cargando' && estado.fase !== 'no-encontrado' && estado.fase !== 'error' && estado.fase !== 'formulario' && estado.fase !== 'listo' && !descartado;
  useEffect(() => {
    reportarPendienteFirma?.(pendienteFirma);
  }, [reportarPendienteFirma, pendienteFirma]);
  useEffect(() => () => reportarPendienteFirma?.(false), [reportarPendienteFirma]);

  useEffect(() => {
    let cancelado = false;
    medplum
      .searchOne('Questionnaire', { identifier: `http://logiacare.ec/fhir/formularios-msp|${CODIGO}` })
      .then((q) => {
        if (cancelado) return;
        setEstado(q ? { fase: 'formulario', questionnaire: q } : { fase: 'no-encontrado' });
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [medplum]);

  const handleSubmitQuestionnaire = useCallback(
    async (response: QuestionnaireResponse) => {
      if (estado.fase !== 'formulario') return;
      const guardado = await medplum.createResource<QuestionnaireResponse>(response);
      setEstado({ fase: 'firma-trabajador', questionnaire: estado.questionnaire, response: guardado });
    },
    [medplum, estado]
  );

  const handleFirmaTrabajador = useCallback(
    async (firma: FirmaPacienteCapturada) => {
      if (estado.fase !== 'firma-trabajador') return;
      const { questionnaire, response } = estado;
      setEstado({ fase: 'procesando', mensaje: t('femo.subiendoFirmaTrabajador') });
      try {
        const { bytes, contentType } = dataUrlABytes(firma.dataUrl, firma.contentType);
        const binario = await medplum.createBinary({ data: bytes, contentType, filename: `firma-trabajador-${firma.metodo}`, securityContext: subject }); // MT-042: el paciente, para la regla de vínculo asistencial
        const docRef = await medplum.createResource<DocumentReference>({
          resourceType: 'DocumentReference',
          status: 'current',
          subject,
          type: { text: `Firma del trabajador (${firma.metodo})` },
          author: [subject],
          content: [
            {
              attachment: {
                contentType: binario.contentType,
                url: `Binary/${binario.id}`,
                title: `Firma del trabajador — ${firma.metodo}`,
              },
            },
          ],
        });
        setEstado({ fase: 'firma-profesional', questionnaire, response, firmaTrabajadorDocRefId: docRef.id!, expedienteFirmado: false, certificadoFirmado: false });
      } catch (err) {
        setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      }
    },
    [medplum, subject, estado, t]
  );

  const handleDescartar = useCallback(() => {
    setDescartado(true);
  }, []);

  /** Expediente (123) y certificado (081) son dos documentos firmados por separado (dos `PanelFirmaImpresion`, ver JSX) — recién pasa a "listo" cuando ambos quedaron firmados. */
  const handleFirmado = useCallback((cual: 'expediente' | 'certificado') => {
    setEstado((prev) => {
      if (prev.fase !== 'firma-profesional') return prev;
      const siguiente = { ...prev, expedienteFirmado: prev.expedienteFirmado || cual === 'expediente', certificadoFirmado: prev.certificadoFirmado || cual === 'certificado' };
      return siguiente.expedienteFirmado && siguiente.certificadoFirmado ? { fase: 'listo' } : siguiente;
    });
  }, []);

  if (especialidad.cargando) {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  // Fail-closed cuando la especialidad no resolvió — ver el comentario largo
  // en FormularioDiscretoPage.tsx (mismo bug real, mismo fix, en los 5
  // gates del catálogo).
  if (!especialidad.codigo || !codigosFormularioDeEspecialidad(especialidad.codigo).includes(CODIGO)) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.fueraDeEspecialidadTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.fueraDeEspecialidadDetalle')}</AlertDescription>
      </Alert>
    );
  }

  if (estado.fase === 'cargando') {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  if (estado.fase === 'no-encontrado') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.noEncontradoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.noEncontradoDetalle', { codigo: CODIGO })}</AlertDescription>
      </Alert>
    );
  }
  if (estado.fase === 'error') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.errorTitulo')}</AlertTitle>
        <AlertDescription>{estado.mensaje}</AlertDescription>
      </Alert>
    );
  }

  if (descartado) {
    return (
      <Alert>
        <AlertTitle>{t('formulario.descartadoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.descartadoDetalle')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-medium text-hce-ink">{t('hub.formulario.FEMO')}</h3>
          <InsigniaFormularioPropio codigo={CODIGO} />
        </div>
        {estado.fase === 'formulario' && (
          <Button type="button" variant="outline" size="sm" onClick={handleDescartar}>
            {t('formulario.descartar')}
          </Button>
        )}
      </div>
      <Indicador fase={estado.fase} />

      {estado.fase === 'formulario' && (
        <FormularioQuestionnaire
          questionnaire={estado.questionnaire}
          subject={subject}
          encounter={encounter}
          textoBoton={t('femo.continuar')}
          textoBotonEnviando={t('formulario.guardando')}
          onSubmit={handleSubmitQuestionnaire}
        />
      )}

      {estado.fase === 'firma-trabajador' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-hce-muted">{t('femo.instruccionFirmaTrabajador')}</p>
          <CapturaFirmaPaciente onCapturada={(firma) => void handleFirmaTrabajador(firma)} />
        </div>
      )}

      {estado.fase === 'firma-profesional' && (
        <div className="flex max-w-md flex-col gap-4">
          <p className="text-sm text-hce-muted">{t('femo.instruccionFirmaProfesional')}</p>

          {!estado.expedienteFirmado && (
            <div className="flex flex-col gap-2 rounded-md border border-border p-3">
              <h4 className="text-sm font-medium text-hce-ink">{t('femo.expediente123')}</h4>
              <PanelFirmaImpresion
                documentoId={`QuestionnaireResponse/${estado.response.id}/expediente`}
                tipoDocumento="FEMO-123"
                titulo={t('femo.expediente123')}
                subject={subject}
                especificacionBuilder={async () => {
                  const paciente = await medplum.readReference(subject);
                  return { modo: 'femo-expediente', response: estado.response, paciente: datosPacienteFemo(paciente, t), huboFirmaTrabajador: true };
                }}
                onFirmado={async () => handleFirmado('expediente')}
              />
            </div>
          )}
          {estado.expedienteFirmado && (
            <Alert>
              <AlertTitle>{t('femo.expediente123')}</AlertTitle>
              <AlertDescription>{t('femo.yaFirmado')}</AlertDescription>
            </Alert>
          )}

          {!estado.certificadoFirmado && (
            <div className="flex flex-col gap-2 rounded-md border border-border p-3">
              <h4 className="text-sm font-medium text-hce-ink">{t('femo.certificado081')}</h4>
              <PanelFirmaImpresion
                documentoId={`QuestionnaireResponse/${estado.response.id}/certificado`}
                tipoDocumento="FEMO-081"
                titulo={t('femo.certificado081')}
                subject={subject}
                especificacionBuilder={async () => {
                  const paciente = await medplum.readReference(subject);
                  return { modo: 'femo-certificado', response: estado.response, paciente: datosPacienteFemo(paciente, t), huboFirmaTrabajador: true };
                }}
                onFirmado={async () => handleFirmado('certificado')}
              />
            </div>
          )}
          {estado.certificadoFirmado && (
            <Alert>
              <AlertTitle>{t('femo.certificado081')}</AlertTitle>
              <AlertDescription>{t('femo.yaFirmado')}</AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {estado.fase === 'procesando' && <p className="text-sm text-hce-muted">{estado.mensaje}</p>}

      {estado.fase === 'listo' && (
        <Alert>
          <AlertTitle>{t('femo.listoTitulo')}</AlertTitle>
          <AlertDescription>{t('femo.listoDetalle')}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
